import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Card, Row, Col, Statistic, Table, Tag, Tooltip, Progress, Space, Typography, Drawer,
    Segmented, Empty, Spin, Grid, Button, Badge, message, theme, InputNumber
} from 'antd';
import { SyncOutlined, WarningOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import evmAdminService from '../../../shared/services/evmAdminService';
import PageHeader from '../../../shared/components/PageHeader';
import { TrafficStats } from '../../../shared/components/evm/vmdetail/MonitorTab';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const REFRESH_MS = 60000;
const RANGES = ['1h', '6h', '24h', '7d', '30d'];

const LEVEL = {
    critical: { color: 'error', label: '严重', icon: <ExclamationCircleOutlined /> },
    error: { color: 'error', label: '错误', icon: <ExclamationCircleOutlined /> },
    warning: { color: 'warning', label: '警告', icon: <WarningOutlined /> },
    warn: { color: 'warning', label: '警告', icon: <WarningOutlined /> },
    info: { color: 'processing', label: '提示', icon: <InfoCircleOutlined /> },
};

const fmtTime = (v) => {
    if (!v) return '-';
    const s = typeof v === 'number' ? new Date(v * 1000).toISOString() : String(v);
    return s.replace('T', ' ').slice(0, 19);
};

const fmtAgo = (v) => {
    if (!v) return '-';
    const t = typeof v === 'number' ? v * 1000 : Date.parse(String(v).replace(' ', 'T'));
    if (!t || Number.isNaN(t)) return String(v);
    const s = Math.max(0, Math.round((Date.now() - t) / 1000));
    if (s < 60) return `${s} 秒前`;
    if (s < 3600) return `${Math.floor(s / 60)} 分钟前`;
    if (s < 86400) return `${Math.floor(s / 3600)} 小时前`;
    return `${Math.floor(s / 86400)} 天前`;
};

const fmtGbFromMb = (mb) => (mb == null ? '-' : `${(Number(mb) / 1024).toFixed(1)}G`);

const poolLow = (p) => {
    const total = Number(p.total) || 0;
    const free = Number(p.free) || 0;
    if (!total) return false;
    // Same rule as the backend EvmInfraMonitor: small pools only alert when empty.
    return free < Math.min(10, Math.max(1, Math.floor(total / 4)));
};

const storageLow = (s) => {
    if (s.total_gb == null || !Number(s.total_gb)) return false;
    return Number(s.used_gb || 0) / Number(s.total_gb) >= 0.85;
};

const fmtBytes = (b) => {
    const n = Number(b) || 0;
    if (n < 1024) return `${n} B`;
    const u = ['KB', 'MB', 'GB', 'TB'];
    let v = n / 1024; let i = 0;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(1)} ${u[i]}`;
};

const fmtAge = (s) => {
    const n = Number(s);
    if (s == null || n < 0) return '从未';
    if (n < 60) return `${n} 秒`;
    if (n < 3600) return `${Math.floor(n / 60)} 分钟`;
    return `${Math.floor(n / 3600)} 小时`;
};

// WireGuard 隧道: 正常对端 / 全部对端
const WgCell = ({ wg }) => {
    if (!wg) return <Text type="secondary">—</Text>;
    if (!wg.capable) return <Tooltip title={wg.error || undefined}><Tag>未启用</Tag></Tooltip>;
    const peers = wg.peers || [];
    if (!peers.length) return <Text type="secondary">0</Text>;
    const bad = peers.filter((p) => p.stale).length;
    return <Tag color={bad ? 'error' : 'success'}>{peers.length - bad}/{peers.length}</Tag>;
};

const WgPeers = ({ wg }) => (
    <Table
        size="small"
        rowKey="node_id"
        pagination={false}
        scroll={{ x: 'max-content' }}
        dataSource={wg?.peers || []}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无对端" /> }}
        columns={[
            { title: '对端', dataIndex: 'name', render: (v, p) => <span>{v}<Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace', marginLeft: 6 }}>{p.endpoint}</Text></span> },
            { title: '握手', dataIndex: 'handshake_age', render: (v, p) => <Tag color={p.stale ? 'error' : 'success'}>{fmtAge(v)}</Tag> },
            { title: '收 / 发', key: 'rxtx', render: (_, p) => `${fmtBytes(p.rx_bytes)} / ${fmtBytes(p.tx_bytes)}` },
        ]}
    />
);

const OnlineDot = ({ node, heartbeatSupported }) => {
    if (!heartbeatSupported) return <Text type="secondary">—</Text>;
    if (!node.enabled) return <Badge status="default" text="停用" />;
    return node.online ? <Badge status="success" text="在线" /> : <Badge status="error" text="离线" />;
};

const StoragePools = ({ pools }) => {
    if (!pools?.length) return <Text type="secondary">-</Text>;
    return (
        <Space direction="vertical" size={2} style={{ width: '100%', minWidth: 160 }}>
            {pools.map((s) => {
                const reported = s.total_gb != null && Number(s.total_gb) > 0;
                const pct = reported ? Math.min(100, (Number(s.used_gb || 0) / Number(s.total_gb)) * 100) : 0;
                return (
                    <div key={s.id}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
                            <Tooltip title={s.path}><Text style={{ fontSize: 12 }} ellipsis>{s.name}</Text></Tooltip>
                            {reported
                                ? <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{Number(s.used_gb || 0).toFixed(0)}/{Number(s.total_gb).toFixed(0)}G</Text>
                                : <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{s.configured_gb ?? '-'}G · 未上报</Text>}
                        </div>
                        {reported && (
                            <Progress percent={Number(pct.toFixed(1))} size="small" showInfo={false}
                                status={pct >= 85 ? 'exception' : 'normal'} style={{ margin: 0 }} />
                        )}
                    </div>
                );
            })}
        </Space>
    );
};

const IpPools = ({ pools }) => {
    if (!pools?.length) return <Text type="secondary">-</Text>;
    return (
        <Space size={[4, 4]} wrap style={{ maxWidth: 280 }}>
            {pools.map((p) => (
                <Tooltip key={p.id} title={`${p.name} · IPv${p.version}${p.status && p.status !== 'enable' ? ` · ${p.status}` : ''}`}>
                    <Tag color={poolLow(p) ? 'error' : undefined} style={{ marginInlineEnd: 0 }}>
                        v{p.version} {p.free}/{p.total}
                    </Tag>
                </Tooltip>
            ))}
        </Space>
    );
};

const HostCell = ({ host }) => {
    if (!host) return <Text type="secondary">—</Text>;
    const memPct = host.mem_total_mb ? (host.mem_used_mb / host.mem_total_mb) * 100 : null;
    return (
        <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
            <span>CPU {host.cpu != null ? `${Number(host.cpu).toFixed(1)}%` : '—'}{host.load1 != null ? ` · load ${Number(host.load1).toFixed(2)}` : ''}</span>
            <span>内存 {memPct != null ? `${memPct.toFixed(1)}% (${fmtGbFromMb(host.mem_used_mb)}/${fmtGbFromMb(host.mem_total_mb)})` : '—'}</span>
        </Space>
    );
};

const VmCounts = ({ node }) => {
    const total = node.vms?.total ?? 0;
    const by = node.vms?.by_status && !Array.isArray(node.vms.by_status) ? node.vms.by_status : {};
    const active = by.active || 0;
    const other = Object.entries(by).filter(([k]) => k !== 'active');
    return (
        <Tooltip title={other.length ? other.map(([k, v]) => `${k}: ${v}`).join(' · ') : null}>
            <span>
                {active}/{total}
                {node.vm_limit ? <Text type="secondary"> (上限 {node.vm_limit})</Text> : null}
                {other.length > 0 && <Text type="warning"> *</Text>}
            </span>
        </Tooltip>
    );
};

// ---------- 节点指标抽屉 ----------
const fmtBits = (v) => {
    const n = Number(v) || 0;
    if (n >= 1e9) return `${(n / 1e9).toFixed(2)} Gbps`;
    if (n >= 1e6) return `${(n / 1e6).toFixed(1)} Mbps`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(0)} Kbps`;
    return `${Math.round(n)} bps`;
};
const UPLINK_RANGE = { '1h': '1h', '6h': '24h', '24h': '24h', '7d': '7d', '30d': '30d' };

/** Physical uplink (and IPv6 VLAN uplink) bandwidth, month 95th percentile, alert threshold. */
const UplinkPanel = ({ node, range, open }) => {
    const { token } = theme.useToken();
    const [data, setData] = useState(null);
    const [alertMbps, setAlertMbps] = useState(null);
    const load = useCallback(async () => {
        if (!node) return;
        const res = await evmAdminService.getNodeUplink(node.id, UPLINK_RANGE[range] || '24h');
        if (res?.status === 'success') {
            setData(res.data);
            setAlertMbps(res.data.alert_mbps || null);
        }
    }, [node, range]);
    useEffect(() => { if (open) load(); }, [open, load]);
    if (!data || data.available === false) return null;
    const roles = Object.entries(data.roles || {});
    const colors = [token.colorPrimary, token.colorSuccess, token.colorWarning, token.colorInfo];
    const series = [];
    roles.forEach(([role, r], i) => {
        const name = role === 'v6' ? `IPv6 ${r.iface}` : r.iface;
        const pts = r.points || [];
        series.push({ name: `${name} ↓`, type: 'line', showSymbol: false, lineStyle: { width: 2, color: colors[i * 2 % 4] }, itemStyle: { color: colors[i * 2 % 4] }, data: pts.map((p) => [p.t, p.rx_bps]) });
        series.push({ name: `${name} ↑`, type: 'line', showSymbol: false, lineStyle: { width: 2, color: colors[(i * 2 + 1) % 4] }, itemStyle: { color: colors[(i * 2 + 1) % 4] }, data: pts.map((p) => [p.t, p.tx_bps]) });
    });
    if (data.alert_mbps > 0 && series.length) {
        series[0].markLine = { symbol: 'none', silent: true, lineStyle: { color: token.colorError, type: 'dashed' }, data: [{ yAxis: data.alert_mbps * 1e6 }], label: { formatter: '告警' } };
    }
    const option = {
        animation: false,
        grid: { left: 64, right: 16, top: 36, bottom: 28 },
        legend: { top: 0, textStyle: { color: token.colorTextSecondary, fontSize: 11 } },
        tooltip: { trigger: 'axis', valueFormatter: (v) => fmtBits(v), backgroundColor: token.colorBgElevated, borderColor: token.colorBorderSecondary, textStyle: { color: token.colorText, fontSize: 12 } },
        xAxis: { type: 'time', axisLabel: { color: token.colorTextSecondary, fontSize: 11, hideOverlap: true }, axisLine: { lineStyle: { color: token.colorBorder } } },
        yAxis: { type: 'value', min: 0, axisLabel: { color: token.colorTextSecondary, fontSize: 11, formatter: fmtBits }, splitLine: { lineStyle: { color: token.colorBorderSecondary } } },
        series,
    };
    const save = async () => {
        const res = await evmAdminService.saveNodeUplink(node.id, alertMbps || 0);
        if (res?.status === 'success') { message.success('已保存'); load(); } else message.error(res?.msg || '保存失败');
    };
    return (
        <div style={{ marginTop: 16 }}>
            <Space size={12} wrap style={{ marginBottom: 8 }}>
                <Text strong>上行带宽</Text>
                {Object.entries(data.p95 || {}).map(([role, p]) => (
                    <Tooltip key={role} title={`本月 5 分钟均值的 95 百分位（${p.samples} 个样本）`}>
                        <Tag>{`${role === 'v6' ? 'IPv6 ' : ''}95th ${fmtBits(p.bps)}`}</Tag>
                    </Tooltip>
                ))}
                <Space size={4}>
                    <Text type="secondary" style={{ fontSize: 12 }}>告警 Mbps</Text>
                    <InputNumber size="small" min={0} value={alertMbps} onChange={setAlertMbps} placeholder="全局" style={{ width: 100 }} />
                    <Button size="small" onClick={save}>保存</Button>
                </Space>
            </Space>
            {series.length ? <ReactECharts option={option} style={{ height: 240 }} notMerge /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="该时段无数据" />}
        </div>
    );
};

const NodeMetricsDrawer = ({ node, open, onClose, isMobile }) => {
    const { token } = theme.useToken();
    const [range, setRange] = useState('24h');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!node) return;
        setLoading(true);
        try {
            const res = await evmAdminService.getNodeMetrics(node.id, range);
            if (res?.status === 'success') setData(res.data || null);
            else { setData(null); message.error(res?.msg || res?.error || '加载指标失败'); }
        } finally {
            setLoading(false);
        }
    }, [node, range]);

    useEffect(() => { if (open) load(); }, [open, load]);
    useEffect(() => { if (!open) setData(null); }, [open]);

    const points = useMemo(() => (Array.isArray(data?.points) ? data.points : []), [data]);

    const makeOption = (title, series, fmt, max) => ({
        animation: false,
        grid: { left: 48, right: 16, top: 32, bottom: 28 },
        title: { text: title, left: 0, top: 0, textStyle: { fontSize: 13, fontWeight: 500, color: token.colorText } },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'line', lineStyle: { color: token.colorTextQuaternary } },
            backgroundColor: token.colorBgElevated,
            borderColor: token.colorBorderSecondary,
            textStyle: { color: token.colorText, fontSize: 12 },
            valueFormatter: (v) => (v == null ? '-' : fmt(v)),
        },
        xAxis: {
            type: 'time',
            axisLine: { lineStyle: { color: token.colorBorder } },
            axisLabel: { color: token.colorTextSecondary, fontSize: 11, hideOverlap: true },
            splitLine: { show: false },
        },
        yAxis: {
            type: 'value',
            min: 0,
            max,
            axisLabel: { color: token.colorTextSecondary, fontSize: 11, formatter: (v) => fmt(v) },
            splitLine: { lineStyle: { color: token.colorBorderSecondary } },
        },
        series: [{
            name: title,
            type: 'line',
            showSymbol: false,
            symbolSize: 8,
            lineStyle: { width: 2, color: token.colorPrimary },
            itemStyle: { color: token.colorPrimary },
            areaStyle: { color: token.colorPrimary, opacity: 0.08 },
            data: series,
        }],
    });

    const charts = useMemo(() => {
        const ts = (p) => Number(p.t) * 1000;
        return [
            makeOption('CPU', points.map((p) => [ts(p), p.cpu]), (v) => `${Number(v).toFixed(1)}%`, 100),
            makeOption('Load (1m)', points.map((p) => [ts(p), p.load1]), (v) => Number(v).toFixed(2)),
            makeOption('内存使用率',
                points.map((p) => [ts(p), p.mem_total_mb ? (p.mem_used_mb / p.mem_total_mb) * 100 : null]),
                (v) => `${Number(v).toFixed(1)}%`, 100),
            makeOption('运行中 VM', points.map((p) => [ts(p), p.vm_running]), (v) => String(Math.round(v))),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [points, token]);

    return (
        <Drawer
            title={node ? `${node.name} · ${node.ip || ''}` : ''}
            open={open}
            onClose={onClose}
            width={isMobile ? '100%' : 760}
            destroyOnClose
            extra={<Button size="small" icon={<SyncOutlined />} onClick={load} loading={loading} />}
        >
            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                <Segmented value={range} onChange={setRange} options={RANGES} />
            </div>
            {loading && !data ? (
                <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
            ) : data && data.available === false ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="监控数据暂未启用" />
            ) : !points.length ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="该时段无数据" />
            ) : (
                <Row gutter={[16, 16]}>
                    {charts.map((opt) => (
                        <Col xs={24} md={12} key={opt.title.text}>
                            <ReactECharts option={opt} style={{ height: 220 }} notMerge />
                        </Col>
                    ))}
                </Row>
            )}
            <UplinkPanel node={node} range={range} open={open} />
            {node?.wireguard && (
                <div style={{ marginTop: 16 }}>
                    <Space size={8} style={{ marginBottom: 8 }} wrap>
                        <Text strong>WireGuard 隧道</Text>
                        <WgCell wg={node.wireguard} />
                        {node.wireguard.overlay_ip && <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{node.wireguard.overlay_ip}</Text>}
                    </Space>
                    <WgPeers wg={node.wireguard} />
                </div>
            )}
        </Drawer>
    );
};

// ---------- 页面 ----------
const NodeMonitor = () => {
    // Platform-wide public traffic (all VMs), hour/day/month.
    const loadTraffic = useCallback((g) => evmAdminService.getTrafficOverview(g), []);
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { token } = theme.useToken();

    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [activeNode, setActiveNode] = useState(null);
    const timer = useRef(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await evmAdminService.getNodesOverview();
            if (res?.status === 'success') {
                setOverview(res.data || {});
                setUpdatedAt(new Date());
            } else if (!silent) {
                message.error(res?.msg || res?.error || '加载失败');
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        timer.current = setInterval(() => {
            if (document.visibilityState === 'visible') load(true);
        }, REFRESH_MS);
        return () => clearInterval(timer.current);
    }, [load]);

    const nodes = useMemo(() => (Array.isArray(overview?.nodes) ? overview.nodes : []), [overview]);
    const alerts = useMemo(() => (Array.isArray(overview?.alerts) ? overview.alerts : []), [overview]);
    const hb = !!overview?.heartbeat_supported;

    const summary = useMemo(() => {
        let online = 0; let running = 0; let storageLocal = 0; let ipLocal = 0;
        nodes.forEach((n) => {
            if (n.online) online++;
            running += n.host?.vm_running ?? (n.vms?.by_status && !Array.isArray(n.vms.by_status) ? (n.vms.by_status.active || 0) : 0);
            storageLocal += (n.storage_pools || []).filter(storageLow).length;
            ipLocal += (n.ip_pools || []).filter(poolLow).length;
        });
        // 后端告警与本地阈值取较大者，避免重复计数
        let storageBackend = 0; let ipBackend = 0;
        alerts.forEach((a) => {
            const t = `${a.type || ''} ${a.target_type || ''}`;
            if (/storage|disk/i.test(t)) storageBackend++;
            else if (/ip/i.test(t)) ipBackend++;
        });
        return {
            online,
            running,
            storageAlerts: Math.max(storageLocal, storageBackend),
            ipAlerts: Math.max(ipLocal, ipBackend),
        };
    }, [nodes, alerts]);

    const columns = [
        {
            title: '节点',
            key: 'name',
            fixed: isMobile ? undefined : 'left',
            render: (_, n) => (
                <Space direction="vertical" size={0}>
                    <a onClick={() => setActiveNode(n)} style={{ fontWeight: 500 }}>{n.name}</a>
                    <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{n.ip || '-'}</Text>
                </Space>
            ),
        },
        {
            title: hb ? '状态' : <Tooltip title="节点心跳待启用"><span>状态 <InfoCircleOutlined /></span></Tooltip>,
            key: 'online',
            render: (_, n) => <OnlineDot node={n} heartbeatSupported={hb} />,
        },
        {
            title: '最后上报',
            key: 'last_seen',
            render: (_, n) => (n.last_seen_at
                ? <Tooltip title={fmtTime(n.last_seen_at)}>{fmtAgo(n.last_seen_at)}</Tooltip>
                : <Text type="secondary">—</Text>),
        },
        { title: 'Agent', dataIndex: 'agent_version', key: 'agent', render: (v) => v || <Text type="secondary">—</Text> },
        { title: '主机 CPU / 内存', key: 'host', render: (_, n) => <HostCell host={n.host} /> },
        { title: 'VM 运行/总数', key: 'vms', render: (_, n) => <VmCounts node={n} /> },
        { title: '存储池', key: 'storage', render: (_, n) => <StoragePools pools={n.storage_pools} /> },
        { title: 'IP 池 (空闲/总数)', key: 'ip', render: (_, n) => <IpPools pools={n.ip_pools} /> },
        ...(nodes.some((n) => n.wireguard) ? [{ title: '隧道', key: 'wg', render: (_, n) => <WgCell wg={n.wireguard} /> }] : []),
    ];

    const statCards = [
        {
            title: '节点在线',
            value: hb ? summary.online : '—',
            suffix: `/ ${nodes.length}`,
            tip: hb ? null : '节点心跳待启用',
        },
        { title: '运行中 VM', value: summary.running },
        { title: '存储池告警', value: summary.storageAlerts, danger: summary.storageAlerts > 0 },
        { title: 'IP 池告警', value: summary.ipAlerts, danger: summary.ipAlerts > 0 },
    ];

    return (
        <div>
            <PageHeader
                title="节点监控"
                extra={
                    <>
                        {updatedAt && <Text type="secondary" style={{ fontSize: 12 }}>{updatedAt.toLocaleTimeString()} 更新</Text>}
                        <Button icon={<SyncOutlined />} onClick={() => load()} loading={loading} />
                    </>
                }
            />

            <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                {statCards.map((c) => (
                    <Col xs={12} md={6} key={c.title}>
                        <Card size="small">
                            <Statistic
                                title={c.tip ? <Tooltip title={c.tip}><span>{c.title} <InfoCircleOutlined /></span></Tooltip> : c.title}
                                value={c.value}
                                suffix={c.suffix}
                                valueStyle={c.danger ? { color: token.colorError } : undefined}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>

            <div style={{ marginBottom: 12 }}>
                <TrafficStats load={loadTraffic} isMobile={isMobile} />
            </div>

            {alerts.length > 0 && (
                <Card size="small" title={`告警 (${alerts.length})`} style={{ marginBottom: 12 }}
                    styles={{ body: { padding: 0, maxHeight: 280, overflowY: 'auto' } }}>
                    {alerts.map((a) => {
                        const lv = LEVEL[String(a.level || '').toLowerCase()] || LEVEL.warning;
                        return (
                            <div key={a.id} style={{
                                display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: isMobile ? 'wrap' : 'nowrap',
                                padding: '8px 12px', borderBottom: `1px solid ${token.colorBorderSecondary}`,
                            }}>
                                <Tag color={lv.color} icon={lv.icon} style={{ marginInlineEnd: 0 }}>{lv.label}</Tag>
                                <span style={{ flex: 1, minWidth: 0 }}>{a.message}</span>
                                <Tooltip title={`首次 ${fmtTime(a.first_seen_at)} · 最近 ${fmtTime(a.last_seen_at)}`}>
                                    <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                        {fmtAgo(a.first_seen_at)}起
                                    </Text>
                                </Tooltip>
                            </div>
                        );
                    })}
                </Card>
            )}

            {isMobile ? (
                loading && !nodes.length ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> : (
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        {!nodes.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无节点" />}
                        {nodes.map((n) => (
                            <Card key={n.id} size="small" hoverable onClick={() => setActiveNode(n)}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 500 }}>{n.name}</div>
                                        <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{n.ip}</Text>
                                    </div>
                                    {hb ? <OnlineDot node={n} heartbeatSupported /> : (
                                        <Tooltip title="节点心跳待启用"><Text type="secondary">—</Text></Tooltip>
                                    )}
                                </div>
                                <div style={{ marginTop: 8, fontSize: 13, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 12, rowGap: 6 }}>
                                    <Text type="secondary">VM</Text><VmCounts node={n} />
                                    <Text type="secondary">主机</Text><HostCell host={n.host} />
                                    <Text type="secondary">上报</Text><span>{n.last_seen_at ? fmtAgo(n.last_seen_at) : '—'}{n.agent_version ? ` · ${n.agent_version}` : ''}</span>
                                    <Text type="secondary">存储</Text><StoragePools pools={n.storage_pools} />
                                    <Text type="secondary">IP</Text><IpPools pools={n.ip_pools} />
                                    {n.wireguard && <><Text type="secondary">隧道</Text><span><WgCell wg={n.wireguard} /></span></>}
                                </div>
                            </Card>
                        ))}
                    </Space>
                )
            ) : (
                <Card styles={{ body: { padding: 0 } }}>
                    <Table
                        columns={columns}
                        dataSource={nodes}
                        rowKey="id"
                        loading={loading && !nodes.length}
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        pagination={nodes.length > 50 ? { pageSize: 50 } : false}
                        onRow={(n) => ({ onDoubleClick: () => setActiveNode(n) })}
                        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无节点" /> }}
                    />
                </Card>
            )}

            <NodeMetricsDrawer
                node={activeNode}
                open={!!activeNode}
                onClose={() => setActiveNode(null)}
                isMobile={isMobile}
            />
        </div>
    );
};

export default NodeMonitor;
